import type { App, ComponentPublicInstance } from "vue";

type AvueCrudInstance = ComponentPublicInstance & {
  tableHeight?: number | string;
  doLayout?: () => void;
};

type ObserverState = {
  resizeObserver: ResizeObserver;
  mutationObserver: MutationObserver;
  frameId: number | null;
};

const observerStates = new WeakMap<object, ObserverState>();

function getOuterHeight(element: HTMLElement | null) {
  if (!element || element.offsetParent === null) return 0;
  const style = window.getComputedStyle(element);
  return (
    element.getBoundingClientRect().height +
    (Number.parseFloat(style.marginTop) || 0) +
    (Number.parseFloat(style.marginBottom) || 0)
  );
}

function setupCrudAutoHeight(crud: AvueCrudInstance) {
  const root = crud.$el;
  if (!(root instanceof HTMLElement) || !root.classList.contains("avue-crud"))
    return;

  const wrapper = root.closest<HTMLElement>(".tab-wr");
  if (!wrapper) return;

  const state: ObserverState = {
    resizeObserver: new ResizeObserver(() => scheduleUpdate()),
    mutationObserver: new MutationObserver(() => {
      observeStructure();
      scheduleUpdate();
    }),
    frameId: null
  };

  function updateHeight() {
    state.frameId = null;
    const table = root.querySelector<HTMLElement>(
      ":scope > .avue-crud__body > .el-card__body > .el-form > .el-table"
    );
    if (!table || wrapper.clientHeight <= 0) return;

    const wrapperStyle = window.getComputedStyle(wrapper);
    const pagination = root.querySelector<HTMLElement>(
      ":scope > .avue-crud__pagination"
    );
    const availableHeight = Math.max(
      80,
      Math.floor(
        wrapper.getBoundingClientRect().bottom -
          table.getBoundingClientRect().top -
          getOuterHeight(pagination) -
          (Number.parseFloat(wrapperStyle.paddingBottom) || 0)
      )
    );

    if (Number(crud.tableHeight) === availableHeight) return;
    crud.tableHeight = availableHeight;
    requestAnimationFrame(() => crud.doLayout?.());
  }

  function scheduleUpdate() {
    if (state.frameId !== null) return;
    state.frameId = requestAnimationFrame(updateHeight);
  }

  function observeStructure() {
    state.resizeObserver.observe(wrapper);
    state.resizeObserver.observe(root);
    root
      .querySelectorAll<HTMLElement>(
        ":scope > *, :scope > .avue-crud__body > .el-card__body > *, :scope > .avue-crud__body > .el-card__body > .el-form > .el-table"
      )
      .forEach(element => {
        state.resizeObserver.observe(element);
      });
  }

  observeStructure();
  state.mutationObserver.observe(root, {
    childList: true,
    subtree: true
  });
  observerStates.set(crud, state);
  scheduleUpdate();
}

function cleanupCrudAutoHeight(crud: AvueCrudInstance) {
  const state = observerStates.get(crud);
  if (!state) return;
  state.resizeObserver.disconnect();
  state.mutationObserver.disconnect();
  if (state.frameId !== null) cancelAnimationFrame(state.frameId);
  observerStates.delete(crud);
}

export function setupAvueTableAutoHeight(app: App) {
  app.mixin({
    mounted() {
      setupCrudAutoHeight(this as unknown as AvueCrudInstance);
    },
    beforeUnmount() {
      cleanupCrudAutoHeight(this as unknown as AvueCrudInstance);
    }
  });
}
